/**
 * Client-side image processing (ADR-0007).
 *
 * The derivative is produced here rather than in a Lambda. The client already
 * holds the full-resolution bitmap at capture time, so resizing on the device
 * removes an entire server path -- no S3 event trigger, no Sharp layer, no cold
 * start, no retry logic -- and the derivative is available offline immediately,
 * before the original has uploaded.
 *
 * **Re-encoding through a canvas is also how EXIF is stripped.** A canvas holds
 * pixels and nothing else, so the GPS coordinates a phone writes into a photo do
 * not survive the round trip. That is `NFR-SEC-9` satisfied as a property of the
 * mechanism rather than as a step someone has to remember.
 *
 * The one thing it costs is orientation: EXIF carries a rotation flag that a
 * naive draw would discard, leaving a sideways photo. `createImageBitmap` applies
 * it when asked, which is why that option is set explicitly below.
 */

/**
 * NFR-PERF-7 bounds an item's display set to 6 MB, and FR-2.3 allows ten photos,
 * so a derivative gets 600 KB. The target below leaves room: a 1600px JPEG at
 * this quality is usually 150-350 KB, and `encodeWithin` steps the quality down
 * if a particular image is not.
 */
export const MAX_DERIVATIVE_BYTES = 600 * 1024;
export const MAX_DERIVATIVE_EDGE = 1600;
const QUALITY_STEPS = [0.82, 0.72, 0.62, 0.5, 0.4] as const;

export interface Derivative {
  readonly blob: Blob;
  readonly width: number;
  readonly height: number;
  readonly contentType: 'image/jpeg';
}

/** Scales to fit within an edge, never up: enlarging adds bytes and no detail. */
export function fitWithin(
  width: number,
  height: number,
  edge: number = MAX_DERIVATIVE_EDGE,
): { width: number; height: number } {
  const longest = Math.max(width, height);
  if (longest <= edge) return { width, height };
  const scale = edge / longest;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/** True when a file is a kind this app will accept as a photo. */
export const isSupportedImage = (type: string): boolean =>
  type === 'image/jpeg' || type === 'image/png' || type === 'image/webp' || type === 'image/heic';

/**
 * Builds the display derivative.
 *
 * Always JPEG, whatever came in. A single output format keeps the content type
 * signed into the upload URL predictable, and means a HEIC photo from a phone is
 * readable on a desktop browser that cannot decode HEIC.
 */
export async function makeDerivative(file: Blob): Promise<Derivative> {
  // `imageOrientation: 'from-image'` applies the EXIF rotation flag. Without it
  // the pixels are correct and the photo is sideways.
  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });

  try {
    const { width, height } = fitWithin(bitmap.width, bitmap.height);
    const { canvas, context } = createSurface(width, height);
    context.drawImage(bitmap, 0, 0, width, height);

    const blob = await encodeWithin(canvas);
    return { blob, width, height, contentType: 'image/jpeg' };
  } finally {
    // Frees the decoded bitmap rather than waiting for collection, which matters
    // on a phone capturing several photos in a row.
    bitmap.close();
  }
}

/** A canvas and the context to draw on it with. */
interface Surface {
  readonly canvas: OffscreenCanvas | HTMLCanvasElement;
  readonly context: OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D;
}

/**
 * The two together, because the context has to be asked for here.
 *
 * `getContext('2d')` on the *union* of the two canvas kinds does not resolve to
 * either one's `'2d'` overload: it falls back to `HTMLCanvasElement`'s
 * `(contextId: string)` signature, whose return type is every kind of context
 * there is -- `ImageBitmapRenderingContext` among them, which has no
 * `drawImage`. Asked for inside each branch, where the canvas is one concrete
 * type, each call picks the overload that means what it says.
 */
function createSurface(width: number, height: number): Surface {
  if (typeof OffscreenCanvas !== 'undefined') {
    const canvas = new OffscreenCanvas(width, height);
    return { canvas, context: drawable(canvas.getContext('2d')) };
  }
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return { canvas, context: drawable(canvas.getContext('2d')) };
}

/**
 * A context, or the same refusal either kind of canvas gets.
 *
 * `null` means the browser would not give out a 2D context -- too many live
 * canvases, or a hardened privacy setting. Thrown from inside `makeDerivative`'s
 * `try`, so the decoded bitmap is still released.
 */
function drawable<T>(context: T | null): T {
  if (context === null) throw new Error('Could not process that image.');
  return context;
}

/**
 * Encodes at the best quality that fits the budget.
 *
 * Steps down rather than guessing once: a photograph of a busy scene compresses
 * far worse than a photo of a shirt against a wall, and a fixed quality would
 * either blow the budget on the first or waste it on the second.
 */
async function encodeWithin(canvas: OffscreenCanvas | HTMLCanvasElement): Promise<Blob> {
  let last: Blob | undefined;
  for (const quality of QUALITY_STEPS) {
    last = await toBlob(canvas, quality);
    if (last.size <= MAX_DERIVATIVE_BYTES) return last;
  }
  // Past the last step the budget is a target rather than a guarantee. Returning
  // the smallest attempt beats refusing to store the photo at all.
  return last!;
}

function toBlob(canvas: OffscreenCanvas | HTMLCanvasElement, quality: number): Promise<Blob> {
  if ('convertToBlob' in canvas) {
    return canvas.convertToBlob({ type: 'image/jpeg', quality });
  }
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) =>
        blob === null ? reject(new Error('Could not process that image.')) : resolve(blob),
      'image/jpeg',
      quality,
    );
  });
}

/**
 * The content type to sign the original upload with.
 *
 * The original is stored exactly as captured, for export at full resolution
 * (FR-11.3) -- so it keeps its own format. HEIC is not in the set the presign
 * contract accepts, so such a file is stored as the JPEG the derivative pass
 * already produced; the alternative is refusing photos from an iPhone.
 */
export function originalContentType(file: Blob): 'image/jpeg' | 'image/png' | 'image/webp' {
  if (file.type === 'image/png') return 'image/png';
  if (file.type === 'image/webp') return 'image/webp';
  return 'image/jpeg';
}
